/**
 * useAuthRouterSync — web SPA 인증 상태 → F7 라우터 자동 sync (Issue #484 Layer 2)
 *
 * app/src/hooks/useAuthRouterSync.ts와 동일 패턴. path 컨벤션만 web에 맞춤
 * (`/login`, `/auth/callback`).
 *
 * NAV_OPTS:
 *   reloadAll + animate:false + clearPreviousHistory
 *   → runtime 전환(로그아웃 등) 시 F7 transition 잔상 차단
 *
 * AUTH_ENTRY_PATHS에는 OAuth round-trip(`/auth/callback`)도 포함 — 콜백이
 * `exchangeCodeForSession` 처리 도중 listener가 즉시 navigate하지 않도록 보호.
 * 콜백 페이지는 자체적으로 `/` 로 navigate하므로 회복 후 동기화는 자연스럽게 진행.
 */

import { useEffect, useRef } from 'react';
import { f7 } from 'framework7-react';
import { useAuth } from '@app/hooks/useAuth';

const LOGIN_PATH = '/login';
const HOME_PATH = '/';
const CALLBACK_PATH = '/auth/callback';

const NAV_OPTS = {
  reloadAll: true,
  animate: false,
  clearPreviousHistory: true,
} as const;

const AUTH_ENTRY_PATHS = new Set<string>([LOGIN_PATH, CALLBACK_PATH]);

export function useAuthRouterSync(): void {
  const { isAuthenticated, isLoading } = useAuth();
  const prevAuth = useRef<boolean | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (prevAuth.current === isAuthenticated) return;
    prevAuth.current = isAuthenticated;

    const router = f7.views.main?.router;
    if (!router) return;

    const currentPath = router.currentRoute?.path;

    if (!isAuthenticated) {
      if (currentPath === CALLBACK_PATH) return;
      router.navigate(LOGIN_PATH, NAV_OPTS);
      return;
    }

    if (currentPath && AUTH_ENTRY_PATHS.has(currentPath)) {
      router.navigate(HOME_PATH, NAV_OPTS);
    }
  }, [isAuthenticated, isLoading]);
}
